import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTask } from '../contexts/TaskContext';

// 日本の祝日判定（主要な祝日）
function isJapaneseHoliday(year, month, day) {
  const holidays = [];
  holidays.push(`${year}-01-01`);
  holidays.push(`${year}-02-11`);
  holidays.push(`${year}-02-23`);
  holidays.push(`${year}-04-29`);
  holidays.push(`${year}-05-03`);
  holidays.push(`${year}-05-04`);
  holidays.push(`${year}-05-05`);
  holidays.push(`${year}-08-11`);
  holidays.push(`${year}-11-03`);
  holidays.push(`${year}-11-23`);

  const secondMonday = (m) => {
    const first = new Date(year, m - 1, 1);
    const dow = first.getDay();
    const firstMon = dow <= 1 ? (1 + (1 - dow + 7) % 7) : (1 + (8 - dow));
    return firstMon + 7;
  };
  const thirdMonday = (m) => secondMonday(m) + 7;

  holidays.push(`${year}-01-${String(secondMonday(1)).padStart(2, '0')}`);
  holidays.push(`${year}-07-${String(thirdMonday(7)).padStart(2, '0')}`);
  holidays.push(`${year}-09-${String(thirdMonday(9)).padStart(2, '0')}`);
  holidays.push(`${year}-10-${String(secondMonday(10)).padStart(2, '0')}`);

  const shunbun = Math.floor(20.8431 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  const shubun = Math.floor(23.2488 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  holidays.push(`${year}-03-${String(shunbun).padStart(2, '0')}`);
  holidays.push(`${year}-09-${String(shubun).padStart(2, '0')}`);

  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return holidays.includes(dateStr);
}

function formatAppDate(dateStr) {
  if (!dateStr) return { text: '', dayOfWeek: -1, isHoliday: false };
  const parts = dateStr.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  const d = new Date(year, month - 1, day);
  const dayOfWeek = d.getDay();
  const weekdays = ['\u65e5', '\u6708', '\u706b', '\u6c34', '\u6728', '\u91d1', '\u571f'];
  const weekday = weekdays[dayOfWeek];
  const holiday = isJapaneseHoliday(year, month, day);
  return {
    text: `${month}\u6708${day}\u65e5\uff08${weekday}\uff09`,
    dayOfWeek,
    isHoliday: holiday,
  };
}

export default function Header({ version }) {
  const { user, login, logout } = useAuth();
  const { online, appDate, setAppDateManually } = useTask();
  const [showMenu, setShowMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const menuRef = useRef(null);
  const datePickerRef = useRef(null);

  const localToday = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const safeAppDate = appDate || localToday;
  const dateInfo = formatAppDate(safeAppDate);

  let dateColorClass = '';
  if (dateInfo.dayOfWeek === 6) dateColorClass = 'date-saturday';
  if (dateInfo.dayOfWeek === 0 || dateInfo.isHoliday) dateColorClass = 'date-holiday';

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showMenu]);

  // 日付ピッカー外クリックで閉じる
  useEffect(() => {
    if (!showDatePicker) return;
    const handleClick = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showDatePicker]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate && setAppDateManually) {
      setAppDateManually(newDate);
      setShowDatePicker(false);
    }
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="app-brand">
          <div className="app-logo">
            <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
              <rect width="64" height="64" rx="14" fill="url(#hg)" />
              <path d="M18 32l8 8 20-20" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              <defs>
                <linearGradient id="hg" x1="0" y1="0" x2="64" y2="64">
                  <stop stopColor="#6c5ce7" />
                  <stop offset="1" stopColor="#a29bfe" />
                </linearGradient>
              </defs>
            </svg>
            <span className="app-title">TaskFlow</span>
          </div>
          {version && <span className="app-version">{version}</span>}
        </div>
        {!online && <span className="offline-badge">{'\u30aa\u30d5\u30e9\u30a4\u30f3'}</span>}
      </div>
      <div className="header-center" ref={datePickerRef}>
        <button
          className={`app-date-large ${dateColorClass}`}
          onClick={() => setShowDatePicker((prev) => !prev)}
        >
          {dateInfo.text}
        </button>
        {showDatePicker && (
          <div className="header-date-picker">
            <label className="date-picker-label">日付を変更</label>
            <input
              type="date"
              className="header-date-input"
              value={safeAppDate}
              onChange={handleDateChange}
            />
            <button
              className="date-picker-today-btn"
              onClick={() => {
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                if (setAppDateManually) {
                  setAppDateManually(todayStr);
                }
                setShowDatePicker(false);
              }}
            >
              今日に戻す
            </button>
          </div>
        )}
      </div>
      <div className="header-right">
        {user ? (
          <div className="user-menu-wrapper" ref={menuRef}>
            <button
              className="avatar-btn"
              onClick={() => setShowMenu((prev) => !prev)}
            >
              <img
                src={user.photoURL || ''}
                alt=""
                className="user-avatar"
                referrerPolicy="no-referrer"
              />
            </button>
            {showMenu && (
              <div className="avatar-dropdown">
                <div className="dropdown-user-info">
                  <span className="dropdown-name">{user.displayName}</span>
                  <span className="dropdown-email">{user.email}</span>
                </div>
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    logout();
                  }}
                >
                  {'\u30ed\u30b0\u30a2\u30a6\u30c8'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="btn btn-primary" onClick={login}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google{'\u3067\u30ed\u30b0\u30a4\u30f3'}
          </button>
        )}
      </div>
    </header>
  );
}
